import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Pencil } from "lucide-react";

// Ajuste de Edição de Usuário — dados CADASTRAIS do usuário (nome, e-mail,
// perfil, situação), num modal próprio, separado de propósito de
// Permissões/Grupos (PermissionsDialog), Meta (GoalDialog) e redefinição
// de senha (nenhuma delas vive aqui). Grupos do Supervisor continuam
// exclusivamente dentro de Permissões — não há seleção de Grupo neste
// modal, para não duplicar essa escolha em dois lugares.
//
// Perfil nunca oferece "admin_master" (mesma lista já usada no formulário
// de criação) — o backend também rejeita explicitamente essa tentativa,
// então isto é só a UI espelhando uma regra que já existe no servidor, não
// o único lugar que a garante.
//
// Situação/Perfil ficam desabilitados ao editar o PRÓPRIO usuário logado —
// mesmo cuidado que o botão "Bloquear" já tem (não deixar o admin se
// autoexcluir/autorrebaixar sem querer); Nome/E-mail continuam editáveis
// para si mesmo, que é o caso de uso normal (corrigir o próprio cadastro).
export default function EditUserDialog({ user: targetUser, open, onOpenChange, onSaved }) {
  const { user: currentUser } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", role: "analista", active: true });
  const [saving, setSaving] = useState(false);
  const isSelf = targetUser && currentUser && targetUser.id === currentUser.id;

  useEffect(() => {
    if (!open || !targetUser) return;
    setForm({
      name: targetUser.name || "",
      email: targetUser.email || "",
      role: targetUser.role || "analista",
      active: targetUser.active !== false,
    });
  }, [open, targetUser]);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Informe o nome"); return; }
    if (!form.email.trim()) { toast.error("Informe o e-mail"); return; }
    setSaving(true);
    try {
      await api.put(`/users/${targetUser.id}`, {
        name: form.name.trim(),
        email: form.email.trim(),
        role: form.role,
        active: form.active,
      });
      toast.success("Usuário atualizado");
      onOpenChange(false);
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (!targetUser) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Pencil size={18} className="text-primary" /> Editar usuário</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label>Nome</Label>
            <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="edituser-name" />
          </div>
          <div>
            <Label>E-mail</Label>
            <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="edituser-email" />
          </div>
          <div>
            <Label>Perfil</Label>
            <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })} disabled={isSelf}>
              <SelectTrigger data-testid="edituser-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Administrador</SelectItem>
                <SelectItem value="supervisor">Supervisor</SelectItem>
                <SelectItem value="analista">Analista</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-3 pt-6">
            <Switch checked={form.active} onCheckedChange={v => setForm({ ...form, active: v })} disabled={isSelf} data-testid="edituser-active" />
            <Label>Ativo</Label>
          </div>
        </div>
        {isSelf && (
          <p className="text-xs text-slate-500 mt-1">Perfil e situação do seu próprio usuário não podem ser alterados por aqui.</p>
        )}
        <p className="text-xs text-slate-400 mt-1">
          Permissões, Grupos do Supervisor, Meta e senha continuam em suas próprias ações, fora deste modal.
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button className="bg-primary hover:bg-primary/90 text-primary-foreground" onClick={save} disabled={saving} data-testid="edituser-save">
            {saving ? "Salvando..." : "Salvar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
